import type { FC } from 'react';

import type { DomainEntry, DomainRef } from './types.ts';

/**
 * Build a `DomainRef` for an entry: a plain handle carrying `.Ref`
 * (the mention — renders the label text verbatim) and `.Def` (the
 * definition — an `<h3>` heading with the same label, then the body).
 *
 * Ref/Def are PURE renderers — no render-time mutation. Catalog
 * invariants (defined-exactly-once, mentioned-at-least-once) are
 * validated by `catalog.validate(rendered)` post-render via text
 * scanning, not by counters bumped during render. This satisfies the
 * React Compiler's purity rules.
 *
 * The mention emits the label as a plain text node, byte-identical to
 * the `.Def` heading text, so the agent reading a mention finds the
 * definition by exact string match. Text (not a `<code>`/`<em>`
 * element) also keeps adjacent punctuation glued: the JSX→markdown
 * reconciler injects a synthetic space between element-text
 * boundaries, so `(<em>label</em>)` would render as `( label)` while
 * text-text adjacency renders `(label)` correctly.
 */
export function makeRef(entry: DomainEntry): DomainRef {
  const Ref: FC = () => <>{entry.label}</>;
  Ref.displayName = `DomainRef.Ref(${entry.label})`;

  const Def: FC = () => (
    <>
      <h3>{entry.label}</h3>
      {entry.body}
    </>
  );
  Def.displayName = `DomainRef.Def(${entry.label})`;

  return { kind: entry.kind, label: entry.label, body: entry.body, Ref, Def };
}
